import React, { useState, useEffect } from 'react';
import { contactService } from '../services/contactService';
import { toast } from 'react-toastify';
import { 
  Eye, 
  Trash2, 
  Search,
  Filter,
  RefreshCw,
  Mail,
  Phone,
  User,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import '../styles/Contacts.css';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await contactService.getContacts();
      if (response.success) {
        setContacts(response.data || []);
      } else {
        toast.error('Failed to fetch contacts');
      }
    } catch (error) {
      toast.error('Error fetching contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);
  };

  const handleUpdateStatus = async (contact, newStatus) => {
    try {
      const response = await contactService.updateContact({
        id: contact.id,
        status: newStatus
      });
      if (response.success) {
        toast.success('Contact status updated successfully');
        fetchContacts();
      } else {
        toast.error('Failed to update contact status');
      }
    } catch (error) {
      toast.error('Error updating contact status');
    }
  };

  const handleDeleteContact = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact message?')) {
      try {
        const response = await contactService.deleteContact(id);
        if (response.success) {
          toast.success('Contact deleted successfully');
          fetchContacts();
        } else {
          toast.error('Failed to delete contact');
        }
      } catch (error) {
        toast.error('Error deleting contact');
      }
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || contact.status === statusFilter;
    const matchesType = typeFilter === 'all' || contact.message_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <Clock size={16} className="status-icon new" />;
      case 'in-progress': return <AlertCircle size={16} className="status-icon in-progress" />;
      case 'resolved': return <CheckCircle size={16} className="status-icon resolved" />;
      default: return <Clock size={16} className="status-icon" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'blue';
      case 'in-progress': return 'orange';
      case 'resolved': return 'green';
      default: return 'gray';
    }
  };

  const getPriorityColor = (messageType) => {
    switch (messageType.toLowerCase()) {
      case 'urgent': return 'red';
      case 'complaint': return 'orange';
      case 'general inquiry': return 'blue';
      case 'support': return 'green';
      default: return 'gray';
    }
  };

  return (
    <div className="contacts-page">
      <div className="page-header">
        <div className="header-left">
          <h1>Contact Messages</h1>
          <p>Manage customer inquiries and support requests</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={fetchContacts}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      <div className="page-controls">
        <div className="search-box">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <div className="filter-box">
            <Filter size={20} className="filter-icon" />
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          <div className="filter-box">
            <select 
              value={typeFilter} 
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="General Inquiry">General Inquiry</option>
              <option value="Support">Support</option>
              <option value="Complaint">Complaint</option>
              <option value="Urgent">Urgent</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading contacts...</div>
      ) : (
        <div className="contacts-list">
          {filteredContacts.map(contact => (
            <div key={contact.id} className="contact-card">
              <div className="contact-header">
                <div className="contact-info">
                  <h3 className="contact-name">
                    <User size={18} />
                    {contact.full_name}
                  </h3>
                  <div className="contact-meta">
                    <span className="contact-email">
                      <Mail size={14} />
                      {contact.email}
                    </span>
                    <span className="contact-phone">
                      <Phone size={14} />
                      {contact.phone_number}
                    </span>
                    <span className="contact-date">
                      <Calendar size={14} />
                      {new Date(contact.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="contact-badges">
                  <span className={`badge ${getPriorityColor(contact.message_type)}`}>
                    {contact.message_type}
                  </span>
                  <span className={`status ${getStatusColor(contact.status)}`}>
                    {getStatusIcon(contact.status)}
                    {contact.status}
                  </span>
                </div>
              </div>

              <div className="contact-content">
                <h4 className="contact-subject">{contact.subject}</h4>
                <p className="contact-message">
                  {contact.message.length > 150 
                    ? `${contact.message.substring(0, 150)}...` 
                    : contact.message
                  }
                </p>
              </div>

              <div className="contact-actions">
                <button 
                  className="btn btn-sm btn-outline"
                  onClick={() => handleViewContact(contact)}
                >
                  <Eye size={14} />
                  View Details
                </button>
                
                {contact.status === 'new' && (
                  <button 
                    className="btn btn-sm btn-primary"
                    onClick={() => handleUpdateStatus(contact, 'in-progress')}
                  >
                    Mark In Progress
                  </button>
                )}
                
                {contact.status === 'in-progress' && (
                  <button 
                    className="btn btn-sm btn-success"
                    onClick={() => handleUpdateStatus(contact, 'resolved')}
                  >
                    Mark Resolved
                  </button>
                )}
                
                <button 
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDeleteContact(contact.id)}
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            </div>
          ))}
          {filteredContacts.length === 0 && (
            <div className="no-data">No contact messages found</div>
          )}
        </div>
      )}

      {/* Contact Details Modal */}
      {showModal && selectedContact && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Contact Message Details</h2>
              <button 
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="contact-details">
                <div className="detail-section">
                  <h3>Contact Information</h3>
                  <div className="detail-grid">
                    <div className="detail-row">
                      <label>Name:</label>
                      <span>{selectedContact.full_name}</span>
                    </div>
                    <div className="detail-row">
                      <label>Email:</label>
                      <span>{selectedContact.email}</span>
                    </div>
                    <div className="detail-row">
                      <label>Phone:</label>
                      <span>{selectedContact.phone_number}</span>
                    </div>
                    <div className="detail-row">
                      <label>Message Type:</label>
                      <span className={`badge ${getPriorityColor(selectedContact.message_type)}`}>
                        {selectedContact.message_type}
                      </span>
                    </div>
                    <div className="detail-row">
                      <label>Status:</label>
                      <span className={`status ${getStatusColor(selectedContact.status)}`}>
                        {getStatusIcon(selectedContact.status)}
                        {selectedContact.status}
                      </span>
                    </div>
                    <div className="detail-row">
                      <label>Received:</label>
                      <span>{new Date(selectedContact.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Message</h3>
                  <div className="message-content">
                    <h4>{selectedContact.subject}</h4>
                    <p>{selectedContact.message}</p>
                  </div>
                </div>

                <div className="detail-actions">
                  {selectedContact.status === 'new' && (
                    <button 
                      className="btn btn-primary"
                      onClick={() => {
                        handleUpdateStatus(selectedContact, 'in-progress');
                        setShowModal(false);
                      }}
                    >
                      Mark In Progress
                    </button>
                  )}
                  
                  {selectedContact.status === 'in-progress' && (
                    <button 
                      className="btn btn-success"
                      onClick={() => {
                        handleUpdateStatus(selectedContact, 'resolved');
                        setShowModal(false);
                      }}
                    >
                      Mark Resolved
                    </button>
                  )}
                  
                  <button 
                    className="btn btn-outline"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;